import classNames from "classnames";
import { CSSProperties } from "react";
import {
	MediaProps,
	PictureProps,
	ResponsiveImagesMap,
	TitleAndContentProps,
} from "../../../constants/pageSectionTypes";
import MarkdownRenderer from "../../common/MarkdownRenderer/MarkdownRenderer";
import { platformUrl } from "../../layout/Navigation/Navigation";
import ContentList from "../../ui/ContentList/ContentList";
import ContentListItem from "../../ui/ContentListItem/ContentListItem";
import Link from "../../ui/Link/Link";
import Picture from "../../ui/Picture/Picture";
import { getPlatform } from "../../utils";
import "./Hero.scss";

type HeroAction = {
	label: string;
	url: string | platformUrl;
	variant?: "primary" | "secondary";
	external?: boolean;
};

export type HeroContent = {
	enabled: boolean;
	background: { mobile: string; desktop: string };
	title: string;
	content?: string;
	actions?: HeroAction[];
	media?: MediaProps[];
	boxes?: Pick<TitleAndContentProps, "content">[];
};

const CallsToAction = ({ actions }: { actions: HeroAction[] }) => {
	return (
		<div className="hero__actions" role="group" aria-label="Hero actions">
			{actions.map((action) => {
				let url: string;
				if (typeof action.url === "string") {
					url = action.url;
				} else if (action.url && (action.url as platformUrl).platform) {
					const platform = getPlatform();
					url = platform.isAndroid
						? (action.url as platformUrl).platform.android
						: (action.url as platformUrl).platform.ios;
				} else {
					url = String(action.url ?? "");
				}
				const isExternal = action?.external ?? false;
				return (
					<Link
						key={action.label}
						link={{ label: action.label, url }}
						isExternal={isExternal}
						className={classNames("link--button", {
							"primary-action text-dark": action?.variant === "primary",
							"text-light": action?.variant !== "primary",
						})}
					/>
				);
			})}
		</div>
	);
};

const Hero = ({
	sectionContent,
	isMobile,
	bgImageMobile,
	bgImageDesktop,
	mediaMap,
	hideMedia,
}: {
	sectionContent: HeroContent;
	isMobile: boolean;
	bgImageMobile?: string;
	bgImageDesktop?: string;
	mediaMap?: ResponsiveImagesMap;
	hideMedia?: boolean;
}) => {
	const hasMedia = sectionContent?.media && sectionContent?.media?.length > 0;
	const hasBoxes = sectionContent?.boxes && sectionContent?.boxes?.length > 0;

	return (
		<section
			className="hero with-cover-image"
			style={{
				backgroundImage: `url(${isMobile ? bgImageMobile : bgImageDesktop || bgImageMobile})`,
			}}>
			<div
				className={classNames("row row--column-start", {
					"row--with-media": hasMedia,
					"row--large-screen-grid": hasBoxes,
				})}>
				<div className="column hero__text-content">
					<MarkdownRenderer>{sectionContent.title}</MarkdownRenderer>
					{sectionContent.content && (
						<MarkdownRenderer>{sectionContent.content}</MarkdownRenderer>
					)}
					{sectionContent.actions && (
						<CallsToAction actions={sectionContent.actions} />
					)}
				</div>
				{hasMedia && !hideMedia && (
					<div className="column hero__media-container">
						<div className="hero__media-content">
							{sectionContent.media!.map((item, index) => {
								const itemStyle = {
									"--grid-row": Array.isArray(item.gridRow)
										? item.gridRow[0]
										: item.gridRow || "1/-1",
									"--grid-column": Array.isArray(item.gridColumn)
										? item.gridColumn[0]
										: item.gridColumn || "1/-1",
									...(Array.isArray(item.gridRow)
										? { "--grid-row-desktop": item.gridRow[1] }
										: {}),
									...(Array.isArray(item.gridColumn)
										? { "--grid-column-desktop": item.gridColumn[1] }
										: {}),
									"--aspect-ratio": item.aspectRatio || "auto",
								} as CSSProperties;
								const mediaWithMultipleSources =
									mediaMap &&
									mediaMap[item.img as keyof typeof mediaMap] &&
									typeof mediaMap[item.img as keyof typeof mediaMap] ===
										"object" &&
									"mobile" in
										(mediaMap as PictureProps)[
											item.img as keyof typeof mediaMap
										];

								if (!mediaMap || !mediaWithMultipleSources) {
									return (
										<img
											src={
												mediaMap
													? (mediaMap as Record<string, string>)[
															item.img as keyof typeof mediaMap
														]
													: item.img
											}
											className="hero__img"
											style={itemStyle}
											alt={item.alt}
										/>
									);
								}

								const mediaValue = (mediaMap as PictureProps)[
									item.img as keyof typeof mediaMap
								];

								return (
									<Picture
										className="hero__img"
										mobileSrc={mediaValue.mobile}
										desktopSrc={mediaValue.desktop}
										style={
											{
												...itemStyle,
												"--animation-delay": `${index * 0.2}s`,
											} as CSSProperties
										}
										sizes={item.sizes}
									/>
								);
							})}
						</div>
					</div>
				)}
				{hasBoxes && (
					<ContentList className="content-list--flex hero__boxes-container">
						{sectionContent.boxes!.map((item, index) => (
							<ContentListItem
								key={index}
								style={
									{ "--animation-delay": `${index * 0.2}s` } as CSSProperties
								}
								className="content-list__item--with-background content-list__item--text">
								<MarkdownRenderer>{item.content}</MarkdownRenderer>
							</ContentListItem>
						))}
					</ContentList>
				)}
			</div>
		</section>
	);
};

export default Hero;
